import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Ceo from '../../assets/images/ceo.jpg';
import GalleryCultureTouris from './GalleryCultureTouris';
import MenuServices from './MenuServices';
import './ServicePage.css';

// Palette dorée / glassmorphism
const colors = {
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 80, damping: 15 },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
  hover: {
    scale: 1.02,
    x: 10,
    backgroundColor: 'rgba(198,156,109,0.1)',
    transition: { type: 'spring', stiffness: 300, damping: 12 },
  },
  tap: { scale: 0.98 },
};

const activities = [
  {
    title: "Visites culturelles privées",
    description: "Accès exclusif à des musées, monuments et sites historiques avec guides experts.",
    icon: "🏛️"
  },
  {
    title: "Circuits personnalisés",
    description: "Itinéraires sur mesure selon vos centres d'intérêt : art, histoire, gastronomie, nature.",
    icon: "🗺️"
  },
  {
    title: "Rencontres avec des artisans et créateurs locaux",
    description: "Découverte de savoir-faire rares et authentiques.",
    icon: "👨‍🎨"
  },
  {
    title: "Expériences gastronomiques uniques",
    description: "Dégustations, cours de cuisine avec chefs locaux, visites de domaines viticoles.",
    icon: "🍷"
  },
  {
    title: "Séjours thématiques de luxe",
    description: "Voyages organisés autour de festivals, expositions, traditions locales ou découvertes insolites.",
    icon: "🌟"
  }
];

const ServicePageCultureTour = () => {
  return (
    <>
      <style>{`
        .culture-gold-section {
          position: relative;
          padding: 80px 0;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradient 15s ease infinite;
          overflow: hidden;
        }

        @keyframes goldGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .culture-gold-section::before,
        .culture-gold-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.2;
          z-index: 0;
          pointer-events: none;
        }
        .culture-gold-section::before {
          width: 50vw; height: 50vw;
          background: #F5E1C8;
          top: -10%; left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }
        .culture-gold-section::after {
          width: 40vw; height: 40vw;
          background: #E8CFB5;
          bottom: -10%; right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0,0) scale(1); }
          50% { transform: translate(5%,10%) scale(1.15); }
          100% { transform: translate(-5%,5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
        }

        .glass-card {
          background: ${colors.glassBg};
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 25px;
          box-shadow: ${colors.shadow};
          transition: box-shadow 0.3s;
        }
        .glass-card:hover {
          box-shadow: ${colors.shadowHover};
        }

        .culture-main-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: ${colors.text};
          background: linear-gradient(135deg, ${colors.text}, ${colors.goldDark});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.goldDark}, ${colors.gold});
          border-radius: 2px;
          margin-bottom: 20px;
        }

        .elegant-text {
          font-size: 1.1rem;
          color: ${colors.textSoft};
          line-height: 1.7;
        }

        .activities-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${colors.text};
          margin-bottom: 20px;
        }

        .activity-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          margin-bottom: 20px;
          padding: 15px;
          background: ${colors.glassBg};
          backdrop-filter: blur(12px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 16px;
          box-shadow: ${colors.shadow};
          transition: all 0.3s;
          cursor: default;
        }
        .activity-item:hover {
          background: rgba(255,255,255,0.9);
          border-color: ${colors.gold};
          box-shadow: ${colors.shadowHover};
        }

        .icon-circle {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, ${colors.gold}, ${colors.goldDark});
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .activity-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: ${colors.text};
          margin-bottom: 5px;
        }

        .activity-description {
          font-size: 0.95rem;
          color: ${colors.textSoft};
          margin: 0;
        }

        .call-to-action {
          background: ${colors.glassBg};
          backdrop-filter: blur(16px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 30px;
          box-shadow: ${colors.shadow};
        }

        .action-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${colors.text};
        }

        .action-subtitle {
          color: ${colors.textSoft};
        }

        .btn-premium {
          background: linear-gradient(135deg, ${colors.gold}, ${colors.goldDark});
          color: white;
          border: none;
          padding: 0.8rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          transition: all 0.3s;
          box-shadow: 0 8px 20px rgba(198,156,109,0.3);
        }
        .btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(198,156,109,0.4);
          color: white;
        }

        .btn-arrow {
          font-size: 1.2rem;
        }

        @media (max-width: 992px) {
          .culture-gold-section {
            padding: 60px 0;
          }
        }
      `}</style>

      <section className="culture-gold-section">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <div className="sidebar sidebar-left">
                <MenuServices />

                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <div className="quote-item quote-border glass-card">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.8, duration: 0.6 }}
                      className="mb-3"
                    >
                      Parce que vous méritez le meilleur, simplement.
                    </motion.div>

                    <div className="quote-item-footer d-flex align-items-center">
                      <motion.img
                        className="testimonial-thumb rounded-circle"
                        src={Ceo}
                        alt="CEO"
                        style={{ width: 60, height: 60, objectFit: 'cover' }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1, type: 'spring' }}
                      />
                      <div className="ms-3">
                        <motion.h3
                          className="quote-author mb-0"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.2 }}
                        >
                          Ndagbia Mobutu
                        </motion.h3>
                        <motion.span
                          className="quote-subtext"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.4 }}
                        >
                          CEO, Azyekos Services
                        </motion.span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            <div className="col-xl-8 col-lg-8">
              <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <motion.div className="title-section" variants={cardVariants}>
                  <motion.h1
                    className="culture-main-title"
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, type: 'spring' }}
                  >
                    Culture & Tourisme
                  </motion.h1>
                  <motion.div
                    className="title-underline"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                  />
                </motion.div>

                <motion.div variants={containerVariants}>
                  <div className="col-md-12">
                    <motion.div variants={textVariants}>
                      <p className="elegant-text">
                        Avec Azyekos, découvrez la richesse culturelle et touristique autrement, à travers des expériences exclusives, raffinées et entièrement personnalisées. Nous vous offrons bien plus qu'une simple visite : une immersion unique au cœur des patrimoines, des traditions et des merveilles locales, conçue selon vos envies et vos passions.
                      </p>
                      <p className="elegant-text">
                        Grâce à notre réseau de partenaires prestigieux et à notre expertise en organisation sur mesure, Azyekos vous ouvre les portes d'un tourisme d'exception. Accès privé à des lieux emblématiques, rencontres avec des artisans ou des experts locaux, circuits confidentiels… chaque détail est soigneusement orchestré pour transformer vos voyages en véritables aventures culturelles.
                      </p>
                    </motion.div>
                  </div>
                </motion.div>

                <div className="gap-40"></div>

                <motion.section id="page-slider" className="page-slider" variants={cardVariants}>
                  <GalleryCultureTouris />
                </motion.section>

                <div className="gap-40"></div>

                <motion.section variants={containerVariants}>
                  <div className="col-md-12">
                    <motion.h2 className="activities-title" variants={textVariants}>
                      Voici 5 activités clés du service Culture & Tourisme d'Azyekos :
                    </motion.h2>
                    <div className="activities-list">
                      {activities.map((activity, index) => (
                        <motion.article
                          key={index}
                          className="activity-item"
                          custom={index}
                          variants={listItemVariants}
                          whileHover="hover"
                          whileTap="tap"
                        >
                          <div className="activity-icon">
                            <div className="icon-circle">{activity.icon}</div>
                          </div>
                          <div className="activity-content">
                            <h3 className="activity-title">{activity.title}</h3>
                            <p className="activity-description">{activity.description}</p>
                          </div>
                        </motion.article>
                      ))}
                    </div>
                  </div>
                </motion.section>

                <div className="gap-40"></div>

                <motion.section className="call-to-action" variants={cardVariants}>
                  <div className="row align-items-center">
                    <div className="col-md-8 text-center text-md-start">
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <h3 className="action-title">Prêt à découvrir le monde autrement ?</h3>
                        <p className="action-subtitle">Laissez-nous créer votre aventure culturelle sur mesure</p>
                      </motion.div>
                    </div>
                    <div className="col-md-4 text-center text-md-end mt-3 mt-md-0">
                      <motion.div
                        whileHover={{ scale: 1.05, transition: { type: 'spring', stiffness: 300 } }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Link to="/Contact" className="btn btn-premium">
                          <span>Contactez-nous</span>
                          <motion.span
                            className="btn-arrow"
                            initial={{ x: 0 }}
                            whileHover={{ x: 5 }}
                            transition={{ type: 'spring', stiffness: 400 }}
                          >
                            →
                          </motion.span>
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </motion.section>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicePageCultureTour;